import React from 'react';
import { DollarSignIcon, ClipboardIcon, BoxIcon, ScaleIcon, SparklesIcon, ChevronRightIcon, CheckCircleIcon, XIcon, InfoIcon, CalendarIcon } from './Icons';
import { LemonSqueezyButton } from './LemonSqueezyButton';

export const SaasLanding: React.FC = () => {
    return (
        <div className="bg-white">
            {/* Hero Section */}
            <section className="relative overflow-hidden pt-20 pb-32">
                <div className="max-w-6xl mx-auto px-4 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-pink/10 text-brand-pink rounded-full text-sm font-bold mb-8 animate-fade-in">
                        <SparklesIcon className="w-4 h-4" />
                        <span>Trusted by 500+ Early Access Bakers</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-serif font-bold text-brand-text-title mb-6 leading-[1.1]">
                        The All-In-One <span className="text-brand-pink">Bakery Management</span> Software
                    </h1>
                    <p className="text-xl md:text-2xl text-brand-text-body/80 max-w-3xl mx-auto leading-relaxed mb-12">
                        Stop drowning in spreadsheets. Automate your pricing, inventory, and orders so you can spend more time baking and less time on admin.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <LemonSqueezyButton 
                            checkoutUrl="https://sweetysbakery.lemonsqueezy.com/checkout/buy/5ded6330-c328-4150-9bd0-e978e8042454?embed=1"
                            buttonText="Get Bakery Pro Now - $17"
                            seoText="Buy Bakery Profit Calculator Bundle"
                            className="px-10 py-5 bg-brand-pink text-white rounded-2xl font-bold text-xl shadow-xl shadow-brand-pink/30 hover:scale-105 hover:shadow-2xl transition-all inline-block"
                        />
                        <a href="#demo" className="px-10 py-5 bg-white border-2 border-brand-pink/20 text-brand-text-title rounded-2xl font-bold text-xl hover:bg-brand-pink/5 transition-all flex items-center justify-center">
                            View Live Demo
                        </a>
                    </div>
                    <p className="mt-6 text-sm text-brand-text-body/60 italic">
                        One-time payment. Lifetime access. Zero monthly fees.
                    </p>
                </div>
                {/* Background decorative elements */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full -z-10 opacity-20 pointer-events-none">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-brand-pink rounded-full blur-[120px]" />
                    <div className="absolute bottom-10 right-10 w-96 h-96 bg-brand-pink/40 rounded-full blur-[150px]" />
                </div>
            </section>

            {/* Video Demo Section */}
            <section id="demo" className="py-16 bg-white relative z-20">
                <div className="max-w-5xl mx-auto px-4 text-center">
                    <h2 className="text-3xl font-serif font-bold text-brand-text-title mb-8">See Bakery Pro in Action</h2>
                    <div className="relative w-full aspect-video rounded-[2rem] overflow-hidden shadow-2xl border-4 border-brand-pink/20 bg-brand-pink/5">
                        <iframe 
                            className="absolute top-0 left-0 w-full h-full"
                            src="https://www.youtube.com/embed/9-wiRFrp2Tc" 
                            title="Bakery Pro Demo Video" 
                            frameBorder="0" 
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
                            allowFullScreen
                        ></iframe>
                    </div>
                </div>
            </section>

            {/* Feature Comparison Table */}
            <section className="py-24 bg-brand-pink-light/20">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-serif font-bold text-brand-text-title mb-4">Why Upgrade to Pro?</h2>
                        <p className="text-brand-text-body/70 text-lg">Compare our free tools with the full professional suite.</p>
                    </div>
                    
                    <div className="bg-white rounded-[2.5rem] shadow-xl border border-brand-border overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-brand-text-title text-white">
                                    <th className="py-8 px-8 text-xl font-serif">Features</th>
                                    <th className="py-8 px-8 text-center text-xl font-serif">Free Tools</th>
                                    <th className="py-8 px-8 text-center text-xl font-serif bg-brand-pink">Bakery Pro</th>
                                </tr>
                            </thead>
                            <tbody className="text-brand-text-body">
                                {[
                                    { f: "Recipe Scaling & Costing", free: true, pro: true },
                                    { f: "Ingredient Price Tracking", free: "Basic", pro: "Automatic" },
                                    { f: "Unlimited Saved Recipes", free: false, pro: true },
                                    { f: "Automated Client Invoicing", free: false, pro: true },
                                    { f: "Inventory Low-Stock Alerts", free: false, pro: true },
                                    { f: "Interactive Order Calendar", free: false, pro: true },
                                    { f: "Profit & Loss Dashboards", free: false, pro: true },
                                    { f: "Mobile-First App Access", free: false, pro: true },
                                    { f: "Priority Tech Support", free: false, pro: true },
                                ].map((row, i) => (
                                    <tr key={i} className="border-b border-brand-border hover:bg-brand-pink/5 transition-colors">
                                        <td className="py-6 px-8 font-bold">{row.f}</td>
                                        <td className="py-6 px-8 text-center">
                                            {typeof row.free === 'boolean' ? (
                                                row.free ? <CheckCircleIcon className="w-6 h-6 text-green-500 mx-auto" /> : <XIcon className="w-6 h-6 text-red-300 mx-auto" />
                                            ) : row.free}
                                        </td>
                                        <td className="py-6 px-8 text-center bg-brand-pink/5">
                                            {typeof row.pro === 'boolean' ? (
                                                row.pro ? <CheckCircleIcon className="w-6 h-6 text-brand-pink mx-auto" /> : <XIcon className="w-6 h-6 text-red-300 mx-auto" />
                                            ) : <span className="font-bold text-brand-pink">{row.pro}</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <div className="p-8 bg-brand-pink/5 text-center">
                            <LemonSqueezyButton 
                                checkoutUrl="https://sweetysbakery.lemonsqueezy.com/checkout/buy/5ded6330-c328-4150-9bd0-e978e8042454?embed=1"
                                buttonText="Get Bakery Pro Now"
                                seoText="Buy Bakery Profit Calculator Bundle"
                                className="px-8 py-4 bg-brand-pink text-white rounded-xl font-bold shadow-lg shadow-brand-pink/20 hover:scale-105 transition-all inline-block"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Social Proof Section (Beta Testers) */}
            <section className="py-24">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="flex flex-col md:flex-row items-center gap-16">
                        <div className="flex-1">
                            <h2 className="text-4xl font-serif font-bold text-brand-text-title mb-8">What Our Beta Testers Say</h2>
                            <div className="space-y-8">
                                {[
                                    { 
                                        name: "Jessica M.", 
                                        role: "Home Baker (Cottage Law)", 
                                        quote: "I used to spend 4 hours every Sunday just pricing my orders for the week. With Bakery Pro, it takes me 10 minutes. It's the best $17 I've ever spent." 
                                    },
                                    { 
                                        name: "Marcus T.", 
                                        role: "Artisan Bread Shop", 
                                        quote: "The low-stock alerts saved me! I never run out of bread flour anymore. The dashboard makes me feel like a real CEO." 
                                    }
                                ].map((t, i) => (
                                    <div key={i} className="p-8 bg-white border border-brand-border rounded-[2rem] shadow-sm relative">
                                        <div className="absolute -top-4 -left-4 text-6xl text-brand-pink/20 font-serif leading-none">“</div>
                                        <p className="text-lg italic text-brand-text-body mb-6 relative z-10">{t.quote}</p>
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-brand-pink/10 rounded-full flex items-center justify-center font-bold text-brand-pink">
                                                {t.name[0]}
                                            </div>
                                            <div>
                                                <p className="font-bold text-brand-text-title">{t.name}</p>
                                                <p className="text-sm text-brand-text-body/60">{t.role}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex-1 bg-brand-text-title rounded-[3rem] p-12 text-white relative overflow-hidden">
                            <h3 className="text-3xl font-serif font-bold mb-6">Built by Bakers, For Bakers</h3>
                            <p className="text-white/70 leading-relaxed mb-8">
                                We didn't want another complex SaaS built by silicon valley suits. We built Bakery Pro because we were tired of losing money on underpriced cakes and forgotten inventory.
                            </p>
                            <ul className="space-y-4 mb-8">
                                {["5-Minute Setup", "No Subscription", "Mobile Friendly"].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3">
                                        <CheckCircleIcon className="w-5 h-5 text-brand-pink" />
                                        <span className="font-bold">{item}</span>
                                    </li>
                                ))}
                            </ul>
                            <div className="absolute bottom-0 right-0 w-32 h-32 bg-brand-pink/20 blur-[60px]" />
                        </div>
                    </div>
                </div>
            </section>

            {/* Digital Products Hub Section */}
            <section className="py-24 bg-white border-t border-brand-border">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-serif font-bold text-brand-text-title mb-4">More Digital Products for Bakers</h2>
                        <p className="text-brand-text-body/70 text-lg">Everything you need to run a modern, profitable bakery business.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Coming Soon Items */}
                        {[
                            { title: "AI Prompt Pack for Bakers", desc: "Copy-paste ChatGPT prompts for writing recipe descriptions, emails, and Instagram captions.", icon: <SparklesIcon className="w-8 h-8" /> },
                            { title: "Bakery Order Form Template", desc: "Professional, customizable order forms to streamline your client intake process.", icon: <ClipboardIcon className="w-8 h-8" /> },
                            { title: "Seasonal Pricing Guide", desc: "Strategies for pricing holiday menus, wedding seasons, and flash sales.", icon: <DollarSignIcon className="w-8 h-8" /> },
                            { title: "Social Media Calendar", desc: "365 days of content ideas, hashtags, and posting schedules for bakery growth.", icon: <CalendarIcon className="w-8 h-8" /> }
                        ].map((prod, i) => (
                            <div key={i} className="bg-slate-50 rounded-2xl border border-brand-border p-6 flex flex-col items-center text-center relative overflow-hidden group">
                                <div className="absolute top-3 right-3 bg-stone-200 text-stone-500 text-[10px] uppercase font-bold px-2 py-1 rounded-md tracking-wider">Coming Soon</div>
                                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-stone-400 mb-6 shadow-sm border border-stone-100 group-hover:scale-110 transition-transform">
                                    {prod.icon}
                                </div>
                                <h3 className="font-serif font-bold text-lg text-brand-text-title mb-3">{prod.title}</h3>
                                <p className="text-sm text-brand-text-body/70 mb-6 flex-grow">{prod.desc}</p>
                                <button className="w-full py-2.5 bg-white border-2 border-stone-200 text-stone-400 font-bold rounded-xl text-sm cursor-not-allowed">
                                    Notify Me
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-3xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-serif font-bold text-brand-text-title mb-4">Frequently Asked Questions</h2>
                        <p className="text-brand-text-body/70">Everything you need to know about Bakery Pro.</p>
                    </div>
                    <div className="space-y-4">
                        {[
                            { q: "Is it hard to set up?", a: "Not at all! You can set up your ingredient library and first recipe in less than 5 minutes. We also provide a 'Quick-Start' guide for new users." },
                            { q: "Can I access it on my phone?", a: "Yes! Bakery Pro is fully responsive. You can check your order calendar or scale a recipe while you're in the middle of the kitchen on your phone or tablet." },
                            { q: "What if I have hundreds of recipes?", a: "We offer bulk import tools and expert support to help you migrate your data from spreadsheets into Bakery Pro smoothly." },
                            { q: "Is there a monthly fee?", a: "No. During our early access phase, it's a one-time payment of $17 for lifetime access. No subscriptions, no hidden fees, and you get instant access to all professional tools." },
                            { q: "Do I need to be a math expert?", a: "Nope! That's why we built it. You just enter your ingredient costs and time, and our algorithms do the heavy lifting to find your perfect price." }
                        ].map((faq, i) => (
                            <details key={i} className="group bg-white rounded-2xl border border-brand-border p-6 shadow-sm cursor-pointer open:ring-2 open:ring-brand-pink/20 transition-all">
                                <summary className="flex items-center justify-between font-bold text-lg text-brand-text-title">
                                    {faq.q}
                                    <ChevronRightIcon className="w-5 h-5 text-brand-pink group-open:rotate-90 transition-transform" />
                                </summary>
                                <p className="mt-4 text-brand-text-body/80 leading-relaxed border-t border-brand-border pt-4">
                                    {faq.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

        </div>
    );
};
